import { Observable } from 'rxjs';

import { IModel } from './models';
import { ColumnType, Table } from './types';
import { AccordionType } from '../lib/common';
import { Repository } from '../services';

export enum ViewMode {
  list,
  detail,
  edit,
  create,
  delete,
}

export interface Link {
  link: string;
  text: string;
}

export interface Columns {
  field: string;
  colName?: string;
  visible: boolean;
  type: string | ColumnType;
  hiddenFormField: boolean;
  hiddenTableField: boolean;
  isEditable: boolean;
  elName?: string;
  mask?: string;
  placeholder?: string;
  position?: number;
  allowsMultiple?: boolean;
  source?: Repository<any>;
  sourceParameter?: string;
  sourceDisplayField?: string;
  searchBoxDebounceTime?: string;
  update?: any;
  comparator?: any;
}

export interface IService<T> {
  columns: Columns[];
  url: string;
  delete(id: string): Observable<T[]>;
  update(item: T): Observable<T>;
  save(): Observable<T>;
  get(queryString?): Observable<T>;
  setData(item: T): void;
  getData(): T;
}

export interface IPage<T> {
  actions: string[];
  items: Observable<T[]>;
  columns: Observable<Columns[]>;
  url: string;
  getTitle(): string;
  setColumns(columns: Columns[]): Observable<Columns[]>;
  fetchById(id: string): Observable<T>;
  fetch(queryString?): Observable<Result<T>>;
  add(item: T): Observable<T>;
  addBulk(item, endpoint: string): Observable<T[]>;
  update(item: T): Observable<T>;
  delete(id: string): Observable<T>;
}

export interface Dropdown {
  value: string;
  text: string;
  checked: boolean;
  description?: string;
  multiple?: boolean;
}

export interface ProvinceConfig {
  active: boolean;
  defaultValue: boolean;
  label: string;
  validFor?: string;
  value: string;
  text: string;
}

export class TDropdown implements Dropdown {
  value: string;
  text: string;
  checked: boolean;
  description?: string;

  constructor(
    dd: Dropdown = {
      value: '',
      text: '',
      checked: false,
      description: '',
    }
  ) {
    const { value, text, checked, description } = dd;

    this.value = value;
    this.text = text;
    this.checked = checked;
    this.description = description;
  }
}

export interface PageInformation {
  meta: {
    /**
     * The length of items array (i.e., the amount of items on this page)
     */
    itemCount: number;

    /**
     * The total amount
     */
    totalItems: number;

    /**
     * The requested items per page (i.e., the limit parameter)
     */
    itemsPerPage: number;

    /**
     * The total amount of pages (based on the limit)
     */
    totalPages: number;

    /**
     * The current page this paginator "points" to
     */
    currentPage: number;

    /**
     * The total amount of SomeEntity matching the filter conditions
     */
    totalItemsNoFiltered?: number;
  };

  links?: {
    first: string;
    previous: string;
    next: string;
    last: string;
  };

  source?: string;
}

export interface Result<T> extends PageInformation {
  items: T[];
}

export interface AccordionForm {
  style: 'table' | 'default';
  isEditable: boolean;
  type: string;
  formId: string;
  dropdownData?: any;
  columns: Observable<Columns[]>;
  item: IModel;
}

export interface AlertButton {
  text?: string;
  role?: string;
  cssClass?: string;
  handler?: any;
}

export interface AccordionTable {
  label: string;
  type: string;
  columns: Observable<Columns[]>;
  items: IModel[];
  actions: string[];
}

export interface Accordion {
  id: string;
  label: string;
  icon: string;
  active: boolean;
  componentType: AccordionType;
  component?: AccordionForm | Table;
}

export interface Filters {
  filter: string;
  operator: string;
  value: any;
  type?: string;
  subField?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  always_null?: boolean;
  join?: string;
  subtype?: boolean;
  coid?: string;
}

export interface TerritoryAssignedFilters {
  filters: any;
}

export interface InsightsBodyRequest {
  addressIds: string[];
}
